import api from '../utils/api';

export interface UsageAnalytics {
  storage: {
    total: number;
    used: number;
    available: number;
    percentage: number;
    fileCount: number;
    byFileType: Array<{
      type: string;
      size: number;
      count: number;
      percentage: number;
    }>;
    recentGrowth: Array<{
      date: string;
      size: number;
    }>;
  };
  activity: {
    totalActivities: number;
    countsByType: Record<string, number>;
    activityByDay: Array<{
      date: string;
      count: number;
    }>;
    mostActiveResources: Array<{
      resourceId: string;
      resourceType: string;
      resourceName: string;
      count: number;
      lastActivity: string;
    }>;
  };
  topFiles: Array<{
    id: string;
    name: string;
    size: number;
    accessCount: number;
    lastAccessed: string;
    type: string;
  }>;
  topFolders: Array<{
    id: string;
    name: string;
    fileCount: number;
    size: number;
    lastModified: string;
  }>;
  userActivity: Array<{
    userId: string;
    userName: string;
    activityCount: number;
    lastActivity: string;
  }>;
  timeSeriesData: Array<{
    date: string;
    uploads: number;
    downloads: number;
    shares: number;
    deletions: number;
  }>;
}

const analyticsService = {
  /**
   * Get comprehensive usage analytics
   */
  async getUsageAnalytics(timeRange: string = '7d'): Promise<UsageAnalytics> {
    const response = await api.get(`/analytics/usage?timeRange=${timeRange}`);
    return response.data;
  },

  /**
   * Get storage analytics
   */
  async getStorageAnalytics(): Promise<UsageAnalytics['storage']> {
    const response = await api.get('/analytics/storage');
    return response.data;
  },

  /**
   * Get activity analytics
   */
  async getActivityAnalytics(days: number = 7): Promise<UsageAnalytics['activity']> {
    const response = await api.get(`/analytics/activity?days=${days}`);
    return response.data;
  },

  /**
   * Get top files by access count
   */
  async getTopFiles(limit: number = 10): Promise<UsageAnalytics['topFiles']> {
    const response = await api.get(`/analytics/top-files?limit=${limit}`);
    return response.data.files;
  },

  /**
   * Get top folders by activity
   */
  async getTopFolders(limit: number = 10): Promise<UsageAnalytics['topFolders']> {
    const response = await api.get(`/analytics/top-folders?limit=${limit}`);
    return response.data.folders;
  },

  /**
   * Get user activity summary
   */
  async getUserActivity(limit: number = 10): Promise<UsageAnalytics['userActivity']> {
    const response = await api.get(`/analytics/user-activity?limit=${limit}`);
    return response.data.users;
  },

  /**
   * Get time series activity data
   */
  async getTimeSeriesData(days: number = 7): Promise<UsageAnalytics['timeSeriesData']> {
    const response = await api.get(`/analytics/time-series?days=${days}`);
    return response.data.series;
  },

  /**
   * Get file type distribution
   */
  async getFileTypeDistribution(): Promise<Array<{ type: string; count: number; size: number }>> {
    const response = await api.get('/analytics/file-types');
    return response.data.distribution;
  },

  /**
   * Get access patterns
   */
  async getAccessPatterns(): Promise<{
    hourly: Array<{ hour: number; count: number }>;
    daily: Array<{ day: string; count: number }>;
    weekly: Array<{ week: string; count: number }>;
  }> {
    const response = await api.get('/analytics/access-patterns');
    return response.data.patterns;
  },

  /**
   * Get sharing analytics
   */
  async getSharingAnalytics(): Promise<{
    totalShares: number;
    activeShares: number;
    sharesByType: Record<string, number>;
    popularSharedFiles: Array<{
      id: string;
      name: string;
      shareCount: number;
      accessCount: number;
    }>;
  }> {
    const response = await api.get('/analytics/sharing');
    return response.data;
  },

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(): Promise<{
    averageUploadTime: number;
    averageDownloadTime: number;
    averageSearchTime: number;
    systemUptime: number;
    errorRate: number;
  }> {
    const response = await api.get('/analytics/performance');
    return response.data.metrics;
  },

  /**
   * Export analytics data
   */
  async exportAnalytics(format: 'csv' | 'json' = 'csv', timeRange: string = '30d'): Promise<Blob> {
    const response = await api.get(`/analytics/export?format=${format}&timeRange=${timeRange}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default analyticsService; 